/**
 * BV Product Pinch Zoom
 * Adds pinch-to-zoom + pan to:
 *  1. The main product gallery slides on mobile
 *  2. The full-screen product media modal slides
 *
 * Architecture:
 *  - Touch listeners are on the SLIDE element so they intercept before
 *    the browser's page-zoom or slider scroll handlers.
 *  - CSS transform is applied to the <img> (or inner media element).
 *  - When zoomed > 1x, pan replaces slider swiping.
 *  - Zoom resets when slider moves to a different slide or modal closes.
 *  - Double-tap resets zoom.
 */
(function () {
  'use strict';

  if (!('ontouchstart' in window)) return;

  var MIN_SCALE = 1;
  var MAX_SCALE = 4;

  function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  }

  function pinchDist(t1, t2) {
    var dx = t2.clientX - t1.clientX;
    var dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function initSlideZoom(slide) {
    if (slide._bvZoomInit) return; // prevent double-init
    slide._bvZoomInit = true;

    // In the modal, images may be inside .product__media or .deferred-media
    var img = slide.querySelector('img');
    if (!img) return;

    var scale = 1;
    var lastScale = 1;
    var tx = 0;
    var ty = 0;
    var lastTX = 0;
    var lastTY = 0;
    var startDist = 0;
    var panStartX = 0;
    var panStartY = 0;
    var mode = null;
    var lastTap = 0;

    function setTransform(animate) {
      img.style.transition = animate ? 'transform 0.25s ease' : 'none';
      img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
      img.style.transformOrigin = 'center center';
    }

    function reset(animate) {
      scale = 1; lastScale = 1;
      tx = 0; ty = 0; lastTX = 0; lastTY = 0;
      mode = null;
      slide.style.touchAction = '';
      img.style.touchAction = '';
      img.style.transition = animate ? 'transform 0.25s ease' : '';
      img.style.transform = '';
    }

    function lockSlider() {
      slide.style.touchAction = 'none';
      img.style.touchAction = 'none';
    }

    slide.addEventListener('touchstart', function (e) {
      var touches = e.touches;

      if (touches.length === 2) {
        mode = 'pinch';
        startDist = pinchDist(touches[0], touches[1]);
        lockSlider();
        e.preventDefault();
        e.stopPropagation();

      } else if (touches.length === 1) {
        var now = Date.now();
        if (now - lastTap < 300 && scale > 1) {
          reset(true);
          e.preventDefault();
          return;
        }
        lastTap = now;

        if (scale > 1.05) {
          mode = 'pan';
          panStartX = touches[0].clientX - lastTX;
          panStartY = touches[0].clientY - lastTY;
          lockSlider();
          e.stopPropagation();
        } else {
          mode = null;
        }
      }
    }, { passive: false });

    slide.addEventListener('touchmove', function (e) {
      var touches = e.touches;

      if (mode === 'pinch' && touches.length === 2) {
        var newDist = pinchDist(touches[0], touches[1]);
        scale = clamp(lastScale * (newDist / startDist), MIN_SCALE, MAX_SCALE);
        tx = lastTX;
        ty = lastTY;
        setTransform(false);
        e.preventDefault();
        e.stopPropagation();

      } else if (mode === 'pan' && touches.length === 1 && scale > 1.05) {
        var maxTX = ((scale - 1) / 2) * img.offsetWidth;
        var maxTY = ((scale - 1) / 2) * img.offsetHeight;
        tx = clamp(touches[0].clientX - panStartX, -maxTX, maxTX);
        ty = clamp(touches[0].clientY - panStartY, -maxTY, maxTY);
        setTransform(false);
        e.preventDefault();
        e.stopPropagation();
      }
    }, { passive: false });

    slide.addEventListener('touchend', function (e) {
      if (mode === 'pinch') {
        lastScale = scale;
        lastTX = tx;
        lastTY = ty;
        if (scale < 1.1) {
          reset(true);
        } else {
          lockSlider();
        }
      } else if (mode === 'pan') {
        lastTX = tx;
        lastTY = ty;
      }
      if (e.touches.length === 0 && scale <= 1.05) {
        mode = null;
        slide.style.touchAction = '';
        img.style.touchAction = '';
      }
    }, { passive: true });

    // Reset when slider navigates away from this slide
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class' && !slide.classList.contains('is-active')) {
          reset(false);
        }
      });
    }).observe(slide, { attributes: true });

    // Reset when modal closes
    var modal = slide.closest('.product-media-modal');
    if (modal) {
      modal.addEventListener('modalClosed', function () { reset(false); }, { passive: true });
    }
  }

  // ── Main gallery slides ───────────────────────────────────────────────────
  function initGallery() {
    var wrapper = document.querySelector('.product__media-wrapper');
    if (!wrapper) return;
    wrapper.querySelectorAll('.product__media-item').forEach(function (slide) {
      if (slide.classList.contains('small-hide')) return; // skip hidden desktop slides
      initSlideZoom(slide);
    });
  }

  // ── Modal slides (init when modal opens, since modal may render lazily) ───
  function initModal() {
    document.querySelectorAll('.product-media-modal .slider__slide').forEach(function (slide) {
      // Set touch-action on the modal's scroll container too
      var content = slide.closest('.product-media-modal__content');
      if (content) content.style.touchAction = 'pan-x';
      initSlideZoom(slide);
    });
  }

  // Watch for the modal being opened (it gets aria-hidden removed or dialog becomes visible)
  function observeModal() {
    var modal = document.querySelector('.product-media-modal');
    if (!modal) return;

    // Init immediately in case modal slides are already in DOM
    initModal();

    // Also re-init whenever modal dialog opens (class or hidden attribute changes)
    new MutationObserver(function () {
      initModal();
    }).observe(modal, { attributes: true, subtree: true, attributeFilter: ['class', 'hidden'] });
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  function boot() {
    initGallery();
    observeModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
